import { computed } from "vue"
import { useQuery } from "@tanstack/vue-query"
import { useVisitor } from "@/composables/useVisitor"
import axios from "axios"

const useRegions = () => {
  const { isFetching, data: regions } = useQuery({
    queryKey: ["fetchAllRegions"],
    async queryFn() {
      const { data } = await axios.get("/public/regions")
      return data
    },
    placeholderData: () => ([]),
  })

  const { visitor } = useVisitor()

  const standardizedRegions = computed(() => {
    return regions.value.map((region) => {
      const renamedLocation = {
        Spain: "Madrid",
        UAE: "Dubai",
      }[region.location] || region.location
      const renamedNation = {
        "South America": "North/South America",
        "Canada": "North/South America",
        "US East": "North/South America",
        "US West": "North/South America",
        "Israel": "Middle East",
      }[region.nation] || region.nation

      return {
        ...region,
        nation: renamedNation,
        location: renamedLocation,
        name: `${renamedLocation} - ${renamedNation}`,
      }
    })
  })

  const regionsByCode = computed(() => {
    return standardizedRegions.value.reduce((previousRegion, currentRegion) => ({
      ...previousRegion,
      [currentRegion.code]: currentRegion,
    }), {})
  })

  const regionsByNation = computed(() => {
    const groupedByNation = standardizedRegions.value.reduce((acc, region) => {
      acc[region.nation] = [...(acc[region.nation] || []), region]
      return acc
    }, {})

    return ["Europe", "North/South America", "Asia Pacific", "Middle East", "Africa"]
      .flatMap((nation) => {
        if (groupedByNation[nation]) {
          return [
            { props: { header: nation } },
            { props: { divider: true } },
            ...groupedByNation[nation].sort((a, b) => a.location.localeCompare(b.location)),
          ]
        }
        return []
      })
  })

  const nearestRegion = computed(() => {
    const userLongitude = visitor.value.longitude
    const userLatitude = visitor.value.latitude
    const closestAwsRegion = getClosestAwsRegion(userLongitude, userLatitude)
    return regionsByCode.value[closestAwsRegion] || {}
  })

  const getClosestAwsRegion = (userLong, userLat) => {
    let minDistance = 10000000000
    let closestAwsRegion = "eu-west-1"
    const awsRegionsMap = regions.value
    for (let i = 0; i < awsRegionsMap.length; i += 1) {
      const region = awsRegionsMap[i]
      const distance = distanceBetweenTwoLongLats(userLong, userLat, region.longitude, region.latitude)
      if (distance < minDistance) {
        minDistance = distance
        closestAwsRegion = region.code
      }
    }
    return closestAwsRegion
  }

  const distanceBetweenTwoLongLats = (long1, lat1, long2, lat2) => {
    const R = 6373.0
    const dlonDegrees = long2 - long1
    const dlatDegrees = lat2 - lat1
    const dlon = dlonDegrees * (Math.PI / 180)
    const dlat = dlatDegrees * (Math.PI / 180)
    const a = Math.pow(Math.sin(dlat / 2), 2) + Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.pow(Math.sin(dlon / 2), 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  return {
    isFetching,
    regions: regionsByCode,
    regionsByNation,
    nearestRegion,
  }
}

export {
  useRegions,
}
